import intlTelInput from 'intl-tel-input';
import formCheck from '../forms/check/_index';

/**
 * Get IntlTelInput validation error key from error number
 * @param {number} errorNumber Error number
 * @returns {string|null} Validation error key or null
 */
function getValidationError(errorNumber) {
    const errors = window.intlTelInputUtils.validationError;
    return Object.keys(errors).find((key) => errors[key] === errorNumber);
}

class mPhoneField extends HTMLFieldSetElement {
    constructor() {
        super();

        this.mainInput = this.querySelector('input[type="tel"]');
        this.phonePrefixInput = this.querySelector('[id$="-phoneprefix"]');
        this.phoneCountryInput = this.querySelector('[id$="-phonecountry"]');
        this.phoneInput = this.querySelector('[id$="-phone"]');

        // Setup IntlTelInput instance
        const params = {
            separateDialCode: true,
            // Used for phone # check according to selected country:
            utilsScript: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.19/js/utils.min.js',
            onlyCountries: {},
        };

        // Make sure we remove localization data
        // Just keep flags and international phone suffix
        const countryData = window.intlTelInputGlobals.getCountryData();
        countryData.forEach((country) => {
            country.name = '';
        });
        countryData.sort((a, b) => a.dialCode - b.dialCode); // By prefix

        // Setup restricted countries
        if (this.mainInput.dataset.restrictedCountryList) {
            try {
                params.onlyCountries = JSON.parse(this.mainInput.dataset.restrictedCountryList);
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(`restrictedCountryList could not be parsed: ${e.message}`);
            }
        }

        // Setup preferred countries
        const preferredCountries = this.mainInput.dataset.preferredcountry?.split(',') ?? [];
        if (preferredCountries.length > 0) {
            params.preferredCountries = preferredCountries;
            // eslint-disable-next-line prefer-destructuring
            params.initialCountry = preferredCountries[0];
        }

        this.iti = intlTelInput(this.mainInput, params);

        // The dial code combobox needs its own name: label.htmlFor is the tel
        // input's id, not the label's, so give the label an id and point the
        // combobox at that (the label still applies to the tel input too, via `for`)
        const comboboxEl = this.querySelector('.iti__selected-flag[role="combobox"]');
        const label = this.querySelector('.input-label');
        if (label && comboboxEl) {
            if (!label.id) {
                label.id = `${label.htmlFor}-label`;
            }
            comboboxEl.setAttribute('aria-labelledby', label.id);
        }

        // Setup event listeners
        ['input', 'change', 'focusout', 'countrychange'].forEach((eventType) => {
            this.mainInput.addEventListener(eventType, () => {
                this.iti.promise.then(() => {
                    const { form } = this.mainInput;
                    if (!this.mainInput.required && !this.iti.telInput.value.length) {
                        formCheck.resetFieldValue(this.mainInput);
                        this.clearHiddenFields();
                    } else if (this.iti.isValidNumber()) {
                        formCheck.itiManageValidationValidMessage(this.mainInput);
                        formCheck.manageValidationValid(this.mainInput);
                        this.updateHiddenFields();
                    } else {
                        const validationError = getValidationError(this.iti.getValidationError());
                        // Triggers the customValidityErrorMessage:
                        formCheck.itiManageValidationErrorMessage(this.mainInput, validationError);
                        // Trigger default behavior
                        // (control management, submit button management, error message display):
                        formCheck.manageValidationError(this.mainInput, form?.ctrl);
                        this.clearHiddenFields();
                    }

                    form?.fieldChecked();
                });
            });
        });
        // Setup initial value
        if (this.mainInput.dataset.value) {
            this.iti.setNumber(this.mainInput.dataset.value);
            // On iOS, change event is not triggered so we force it
            this.mainInput.dispatchEvent(new Event('change'));
        }

        // Add black transparent background behind dropdown (only on mobile)
        this.dropdownScrimEl = document.createElement('div');
        this.dropdownScrimEl.className = 'iti-dropdown-scrim';
        this.mainInput.addEventListener('open:countrydropdown', () => {
            if (this.iti.dropdown) {
                this.iti.dropdown.before(this.dropdownScrimEl);
            }
        });
        this.mainInput.addEventListener('close:countrydropdown', () => {
            this.dropdownScrimEl.remove();
        });
    }

    /**
     * Update hidden field values according to main input value
     */
    updateHiddenFields() {
        const { selectedCountryData } = this.iti;
        const phoneValue = this.iti.telInput.value.replaceAll(/[-|\s|.]/g, '');
        this.phonePrefixInput.value = `+${selectedCountryData.dialCode}`;
        this.phoneCountryInput.value = selectedCountryData.iso2;
        this.phoneInput.value = `+${selectedCountryData.dialCode}-${phoneValue}`;
    }

    /**
     * Clear hidden field values
     */
    clearHiddenFields() {
        this.phonePrefixInput.value = '';
        this.phoneCountryInput.value = '';
        this.phoneInput.value = '';
    }
}

customElements.define('m-phone-field', mPhoneField, {
    extends: 'fieldset',
});
